import { useState } from 'react'
import { useUsers } from '@/api/users'
import { ChevronDown, ChevronUp, Clipboard } from 'lucide-react'
import { useResourceAuditLogs } from '@/api/audit-logs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useHasPermission } from '@/lib/hooks/usePermission'

interface ScanAuditLogsCardProps {
  scanId?: string
}

const formatUserLabel = (displayName?: string | null, email?: string | null) => {
  const trimmedDisplayName = displayName?.trim()
  const trimmedEmail = email?.trim()

  if (trimmedDisplayName) {
    return trimmedDisplayName
  }

  if (trimmedEmail) {
    return trimmedEmail
  }

  return 'Onbekende gebruiker'
}

const formatAuditActionSummary = (action: string) => {
  const normalizedAction = action.toLowerCase()

  if (normalizedAction.includes('confirmed')) return 'Analyse bevestigd'
  if (normalizedAction.includes('updated')) return 'Analyse bijgewerkt'
  if (normalizedAction.includes('created')) return 'Analyse aangemaakt'
  if (normalizedAction.includes('deleted')) return 'Analyse verwijderd'

  return action.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

export function ScanAuditLogsCard({ scanId }: ScanAuditLogsCardProps) {
  const [showAuditLogs, setShowAuditLogs] = useState(false)
  const canViewScanAuditLogs = useHasPermission('audit.list.resource')
  const { data: usersData } = useUsers({ page: 1, per_page: 200 })

  const usersById = new Map((usersData?.users ?? []).map(user => [user.id, user]))

  const {
    data: scanAuditLogs,
    isLoading: isScanAuditLogsLoading,
    isFetching: isScanAuditLogsFetching,
    isError: isScanAuditLogsError,
  } = useResourceAuditLogs('Scan', scanId ?? '', 1, 10, canViewScanAuditLogs && showAuditLogs)

  if (!canViewScanAuditLogs) {
    return null
  }

  const getActorLabel = (userId: string) => {
    const user = usersById.get(userId)
    return formatUserLabel(user?.displayName, user?.email)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clipboard className="h-4 w-4" />
            Auditlogs
          </CardTitle>
          <CardDescription>Bekijk de geregistreerde acties voor deze scan.</CardDescription>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => setShowAuditLogs(prev => !prev)}
          aria-expanded={showAuditLogs}
        >
          {showAuditLogs ? 'Sluiten' : 'Openen'}
          {showAuditLogs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </CardHeader>

      {showAuditLogs && (
        <CardContent className="pt-0">
          {isScanAuditLogsLoading || isScanAuditLogsFetching ? (
            <div className="rounded-lg border bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
              Auditlogs laden...
            </div>
          ) : isScanAuditLogsError ? (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-4 text-sm text-destructive">
              Auditlogs konden niet worden geladen.
            </div>
          ) : (scanAuditLogs?.logs ?? []).length > 0 ? (
            <div className="divide-y rounded-lg border bg-background">
              {(scanAuditLogs?.logs ?? []).map(log => (
                <div key={log.id} className="px-3 py-3 sm:px-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="min-w-0 text-sm font-medium text-foreground">
                          {formatAuditActionSummary(log.action)}
                        </p>
                      </div>

                      <p className="text-xs text-muted-foreground">
                        Gebruiker: {getActorLabel(log.userId)}
                      </p>
                    </div>
                    <div className="shrink-0 whitespace-nowrap text-right text-xs text-muted-foreground">
                      <div className="font-medium text-foreground">
                        {new Date(log.createdAt).toLocaleDateString('nl-NL', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {typeof scanAuditLogs?.total === 'number' && scanAuditLogs.total > scanAuditLogs.logs.length && (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  Toont {scanAuditLogs.logs.length} van {scanAuditLogs.total} auditlogs.
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-lg border bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
              Geen auditlogs gevonden voor deze scan.
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
